import type { CalendarSingleView } from '@blocksuite/affine/data-view';
import {
  presentCanvasDatabaseView,
  provisionShardedDatabaseBlockDataSource,
} from '@blocksuite/affine-block-database';
import type { GanttSingleView } from '@blocksuite/data-view/view-presets';
import { deserializeXYWH, serializeXYWH } from '@blocksuite/global/gfx';
import type { Store } from '@blocksuite/store';
import {
  DEFAULT_CANVAS_DATABASE_HEIGHT,
  DEFAULT_CANVAS_DATABASE_WIDTH,
} from '@streamlain/database-identity';
import { PublicationError } from '@streamlain/doc-publication-contract';
import { Service } from '@toeverything/infra';

import type { DocsService } from '../../doc';
import {
  CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
  type ViewBackedAppMutationRecord,
  ViewBackedAppService,
  viewBackedAppSourceRefKey,
} from '../view-backed-app';
import type {
  CalendarApiClientService,
  CalendarMailboxLinkCreateInput,
} from './calendar-api-client';
import {
  type CalendarLocalEventCreateInput,
  type CalendarLocalEventUpdateInput,
  CalendarLocalProjectionService,
} from './calendar-local-projection';
import {
  CalendarMutationOutboxService,
  type CalendarMutationRecord,
  isCalendarMutation,
} from './calendar-mutation-outbox';
import { randomCalendarSourceColor } from './calendar-source-colors';
import { decodeCalendarSourceWireV1 } from './calendar-source-wire';
import type {
  CalendarEventsResponse,
  CalendarInvitationAssignmentsResponse,
  CalendarInvitationResponseStatus,
  CalendarMailboxAreaTransitionAction,
  CalendarMailboxAreaTransitionPreview,
  CalendarMailboxAreaTransitionResource,
  CalendarMailboxAreaTransitionResult,
  CalendarMailboxLink,
  CalendarMailboxLinkPatch,
  CalendarMailboxLinksResponse,
  CalendarMode,
  CalendarProjectedEvent,
  CalendarSource,
  CalendarSourceAttachInput,
  CalendarSourceCandidate,
  CalendarSourceCandidatesResponse,
  CalendarSourcePreferencePatch,
  CalendarSourceRef,
  CalendarSourcesResponse,
  CalendarSourceSyncState,
} from './types';

export interface CalendarCreateLocalInput {
  areaRootFolderId?: string | null;
  databaseId?: string;
  title: string;
  /** False once the visible-calendar limit is reached: the new one stays hidden. */
  visible: boolean;
}

export interface CalendarCreateLocalResult {
  databaseId: string;
  docId: string;
  source: CalendarSource;
  viewId: string;
}

export interface LocalCalendarDatabaseProvision {
  blockId: string;
  databaseId: string;
  viewId: string;
}

export interface CalendarWorkspaceLoadResult<T> {
  data: T | undefined;
  error: unknown | null;
}

export interface CalendarEventCreateResult {
  rowId: string;
  startAt: string;
  syncState: 'completed' | 'failed' | 'pending';
}

interface CalendarMutationOverlay {
  mutation: CalendarMutationRecord;
  syncState: CalendarSourceSyncState;
}

interface InvitationResponseOverlay {
  icsSequence: number | null;
  rowId: string;
  source: CalendarSourceRef;
  status: CalendarInvitationResponseStatus;
}

export class CalendarAppService extends Service {
  readonly pendingMutations$ = this.mutationOutbox.pendingMutations$;
  readonly failedMutations$ = this.mutationOutbox.failedMutations$;
  readonly completed$ = this.mutationOutbox.completed$;
  readonly failed$ = this.mutationOutbox.failed$;
  private readonly invitationResponseOverlays = new Map<
    string,
    InvitationResponseOverlay
  >();

  constructor(
    private readonly docsService: DocsService,
    private readonly calendarApi: CalendarApiClientService,
    private readonly localProjection: CalendarLocalProjectionService,
    private readonly mutationOutbox: CalendarMutationOutboxService,
    private readonly viewBackedApps: ViewBackedAppService
  ) {
    super();
    const failedSubscription = this.mutationOutbox.failed$.subscribe(
      mutation => {
        if (mutation.operation !== 'calendar.respondInvitation') return;
        const payload = mutation.payload as {
          rowId: string;
          source: CalendarSourceRef;
        };
        this.invitationResponseOverlays.delete(
          invitationResponseRefKey(payload.source, payload.rowId)
        );
      }
    );
    this.disposables.push(() => failedSubscription.unsubscribe());
  }

  async listWorkspaceSources(
    workspaceId: string,
    cachedSources: readonly CalendarSource[] = []
  ): Promise<CalendarWorkspaceLoadResult<CalendarSourcesResponse>> {
    const result =
      await this.viewBackedApps.listWorkspaceSources<CalendarSource>({
        decodeSource: decodeCalendarSourceWireV1,
        descriptor: CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
        listLocalSources: async serverSources =>
          (
            await this.localProjection.listLocalSources({
              serverSources,
            })
          ).sources,
        workspaceId,
      });
    if (!result.error) return result;
    return {
      data: {
        sources: this.viewBackedApps.applyPendingSourceMutations(
          CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
          mergeCalendarSources(cachedSources, result.data?.sources ?? [])
        ),
      },
      error: result.error,
    };
  }

  listInvitationAssignments(
    workspaceId: string,
    mailMessageId: string
  ): Promise<CalendarInvitationAssignmentsResponse> {
    return this.calendarApi.listInvitationAssignments(
      workspaceId,
      mailMessageId
    );
  }

  async assignInvitation(
    workspaceId: string,
    mailMessageId: string,
    source: CalendarSource
  ): Promise<{ projected: number; skipped: number }> {
    await this.viewBackedApps.prepareSourceForServerMutation(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      source
    );
    return this.calendarApi.assignInvitation(
      workspaceId,
      mailMessageId,
      source.id
    );
  }

  async listWorkspaceCandidates(
    workspaceId: string,
    sources: readonly CalendarSource[],
    cachedCandidates: readonly CalendarSourceCandidate[] = []
  ): Promise<CalendarWorkspaceLoadResult<CalendarSourceCandidatesResponse>> {
    const result = await this.viewBackedApps.listWorkspaceCandidates({
      descriptor: CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      listLocalCandidates: async () =>
        (
          await this.localProjection.listLocalCandidates({
            serverSources: sources,
          })
        ).candidates,
      sources,
      workspaceId,
    });
    if (!result.error) return result;
    const sourceRefs = new Set(sources.map(viewBackedAppSourceRefKey));
    return {
      data: {
        candidates: mergeCalendarCandidates(
          cachedCandidates,
          result.data?.candidates ?? []
        ).filter(
          candidate => !sourceRefs.has(viewBackedAppSourceRefKey(candidate))
        ),
      },
      error: result.error,
    };
  }

  async projectWorkspaceEvents(input: {
    mode: CalendarMode;
    onLocalEvents?: (events: CalendarEventsResponse) => void;
    /** Canonical response, without waiting for local document hydration. */
    onServerEvents?: (events: CalendarEventsResponse) => void;
    sources: readonly CalendarSource[];
    windowEndAt: string;
    windowStartAt: string;
    workspaceId: string;
    cachedEvents?: CalendarEventsResponse;
  }): Promise<CalendarWorkspaceLoadResult<CalendarEventsResponse>> {
    // Route changes unmount the Planner surface, but its scoped projection
    // cache survives. Paint that already-validated window immediately instead
    // of holding the whole app behind PageDetailLoading.
    if (input.cachedEvents) {
      input.onLocalEvents?.(
        this.applyPendingInvitationResponses(input.cachedEvents, input.sources)
      );
    }

    // A newly received invitation can be absent from the retained local cache.
    // Do not serialize its canonical server projection behind reopening every
    // local calendar document: both projections are independent and must start
    // together. Whichever resolves first can make useful calendar state
    // visible; the final return still reconciles both sources.
    // Latest local projection, partial until every source has settled.
    let resolvedLocalEvents: CalendarEventsResponse | undefined;
    let localProjectionComplete = false;
    let resolvedServerEvents: CalendarEventsResponse | undefined;
    const emitLocalEvents = (localEvents: CalendarEventsResponse) => {
      resolvedLocalEvents = localEvents;
      const reconciledEvents = resolvedServerEvents
        ? mergeCalendarEvents(
            localEvents,
            resolvedServerEvents,
            'local-projection'
          )
        : input.cachedEvents
          ? mergeCalendarEvents(
              localEvents,
              input.cachedEvents,
              'local-projection'
            )
          : localEvents;
      input.onLocalEvents?.(
        this.applyPendingInvitationResponses(reconciledEvents, input.sources)
      );
    };
    const localEventsPromise = this.localProjection.projectLocalEvents(
      {
        mode: input.mode,
        sources: input.sources,
        windowEndAt: input.windowEndAt,
        windowStartAt: input.windowStartAt,
      },
      // Calendars already projected locally show up without waiting for a
      // slow source or the server; the others keep their server/cached rows.
      partial => {
        if (!localProjectionComplete) emitLocalEvents(partial);
      }
    );
    const serverEventsPromise = this.calendarApi.projectEvents(
      input.workspaceId,
      {
        mode: input.mode,
        sourceIds: input.sources.map(source => source.id),
        windowEndAt: input.windowEndAt,
        windowStartAt: input.windowStartAt,
      }
    );

    void localEventsPromise
      .then(localEvents => {
        localProjectionComplete = true;
        emitLocalEvents(localEvents);
      })
      .catch(() => {});
    void serverEventsPromise
      .then(serverEvents => {
        resolvedServerEvents = serverEvents;
        this.reconcileCompletedInvitationResponses(serverEvents, input.sources);
        const reconciledEvents = resolvedLocalEvents
          ? mergeCalendarEvents(
              resolvedLocalEvents,
              serverEvents,
              'local-projection'
            )
          : input.cachedEvents
            ? mergeCalendarEvents(input.cachedEvents, serverEvents, 'cache')
            : serverEvents;
        input.onLocalEvents?.(
          this.applyPendingInvitationResponses(reconciledEvents, input.sources)
        );
        input.onServerEvents?.(serverEvents);
      })
      .catch(() => {});

    const [localResult, serverResult] = await Promise.allSettled([
      localEventsPromise,
      serverEventsPromise,
    ]);
    const localEvents =
      localResult.status === 'fulfilled' ? localResult.value : undefined;
    const serverEvents =
      serverResult.status === 'fulfilled' ? serverResult.value : undefined;

    if (serverEvents) {
      this.reconcileCompletedInvitationResponses(serverEvents, input.sources);
    }

    if (localEvents && serverEvents) {
      return {
        data: this.applyPendingInvitationResponses(
          mergeCalendarEvents(localEvents, serverEvents, 'local-projection'),
          input.sources
        ),
        error: null,
      };
    }

    if (localEvents) {
      return {
        data: this.applyPendingInvitationResponses(
          input.cachedEvents
            ? mergeCalendarEvents(
                localEvents,
                input.cachedEvents,
                'local-projection'
              )
            : localEvents,
          input.sources
        ),
        error: serverResult.status === 'rejected' ? serverResult.reason : null,
      };
    }

    if (serverEvents) {
      return {
        data: this.applyPendingInvitationResponses(
          input.cachedEvents
            ? mergeCalendarEvents(input.cachedEvents, serverEvents, 'cache')
            : serverEvents,
          input.sources
        ),
        error: localResult.status === 'rejected' ? localResult.reason : null,
      };
    }

    return {
      data: input.cachedEvents,
      error:
        localResult.status === 'rejected'
          ? localResult.reason
          : serverResult.status === 'rejected'
            ? serverResult.reason
            : null,
    };
  }

  async createEventInSource(
    source: CalendarSource,
    input: CalendarLocalEventCreateInput
  ): Promise<CalendarEventCreateResult> {
    const created = await this.localProjection.createLocalEvent(source, input);
    await this.mutationOutbox.enqueueReconcileEvent(created.reconcile);
    return {
      rowId: created.rowId,
      startAt: created.startAt,
      syncState: 'pending',
    };
  }

  openCommandSession(source: CalendarSource) {
    return this.localProjection.openCommandSession(source, async input => {
      await this.mutationOutbox.enqueueReconcileEvent(input);
    });
  }

  async updateEventInSource(
    source: CalendarSource,
    input: CalendarLocalEventUpdateInput
  ): Promise<CalendarEventCreateResult> {
    const updated = await this.localProjection.updateLocalEvent(source, input);
    await this.mutationOutbox.enqueueReconcileEvent(updated.reconcile);
    return {
      rowId: updated.rowId,
      startAt: updated.startAt,
      syncState: 'pending',
    };
  }

  async deleteEventInSource(
    source: CalendarSource,
    rowId: string
  ): Promise<CalendarEventCreateResult> {
    const deleted = await this.localProjection.deleteLocalEvent(source, rowId);
    await this.mutationOutbox.enqueueReconcileEvent(deleted.reconcile);
    return {
      rowId: deleted.rowId,
      startAt: deleted.startAt,
      syncState: 'pending',
    };
  }

  observeEventChanges(
    sources: readonly CalendarSource[],
    onChange: () => void
  ): Promise<() => void> {
    return this.localProjection.observeEventChanges(sources, onChange);
  }

  async listMailboxLinks(
    workspaceId: string,
    source: CalendarSource,
    cachedLinks: readonly CalendarMailboxLink[] = []
  ): Promise<CalendarWorkspaceLoadResult<CalendarMailboxLinksResponse>> {
    if (source.syncState === 'local' || source.area === null) {
      return {
        data: {
          decisions: [],
          links: this.applyPendingMailboxLinkMutations(source, []),
        },
        error: null,
      };
    }

    try {
      const response = await this.calendarApi.listMailboxLinks(
        workspaceId,
        source
      );
      return {
        data: {
          decisions: response.decisions ?? [],
          links: this.applyPendingMailboxLinkMutations(source, response.links),
        },
        error: null,
      };
    } catch (error) {
      return {
        data: {
          decisions: [],
          links: this.applyPendingMailboxLinkMutations(source, cachedLinks),
        },
        error,
      };
    }
  }

  async createLocalCalendar(
    input: CalendarCreateLocalInput
  ): Promise<CalendarCreateLocalResult> {
    const title = normalizeCalendarTitle(input.title);
    const areaRootFolderId = input.areaRootFolderId ?? null;
    const provisionedRef: {
      current?: Promise<LocalCalendarDatabaseProvision>;
    } = {};
    const record = this.docsService.createDoc({
      createInWorkspaceRoot: !areaRootFolderId,
      primaryMode: 'edgeless',
      targetFolderId: areaRootFolderId ?? undefined,
      title,
      docProps: {
        onStoreLoad: (doc, { noteId }) => {
          const pageId = doc.root?.id;
          if (!pageId) {
            throw new Error('Calendar canvas root was not initialized.');
          }
          provisionedRef.current = provisionLocalCalendarDatabase(doc, {
            besideBlockId: noteId,
            databaseId: input.databaseId,
            parentId: pageId,
            title,
          });
        },
      },
    });

    const provisioned = await provisionedRef.current;
    if (!provisioned) throw new PublicationError('FORMAT_MISMATCH');

    const color = randomCalendarSourceColor();
    await this.viewBackedApps.createSource(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      {
        areaRootFolderId,
        blockId: provisioned.blockId,
        colorOverride: color,
        databaseId: provisioned.databaseId,
        docId: record.id,
        title,
        viewId: provisioned.viewId,
        visible: input.visible,
      }
    );
    // Project only the new calendar doc; the rest of the workspace is unchanged.
    const projected = await this.localProjection.listLocalSources({
      docIds: [record.id],
      pendingAreaByDocId: areaRootFolderId
        ? new Map([[record.id, areaRootFolderId]])
        : undefined,
    });
    const source = projected.sources.find(
      source =>
        source.docId === record.id &&
        source.databaseId === provisioned?.databaseId &&
        source.viewId === provisioned.viewId
    );
    if (!source) {
      throw new Error('Calendar source was created but not locally projected.');
    }

    // The local document and source are already authoritative for the creating
    // client. Keep canonical server-title persistence in the existing path,
    // but do not hold the local-first result (and its dialog) open while the
    // sync acknowledgement is pending.
    void this.docsService
      .persistDocTitleFromEditor(record.id, title)
      .catch(error => {
        console.error('[PlannerCalendar] Calendar title sync failed', error);
      });

    return {
      databaseId: provisioned.databaseId,
      docId: record.id,
      source: { ...source, color, visible: input.visible },
      viewId: provisioned.viewId,
    };
  }

  attachSource(input: CalendarSourceAttachInput): Promise<unknown> {
    return this.viewBackedApps.attachSource(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      input
    );
  }

  updateSourcePreference(
    sourceId: string,
    patch: CalendarSourcePreferencePatch
  ): Promise<ViewBackedAppMutationRecord> {
    return this.viewBackedApps.updateSourcePreference(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      sourceId,
      patch
    );
  }

  presentSourceOnCanvas(source: CalendarSource): Promise<void> {
    return this.localProjection.presentLocalSourceOnCanvas(source);
  }

  async renameSource(source: CalendarSource, title: string): Promise<void> {
    const normalizedTitle = title.trim();
    if (!normalizedTitle) {
      throw new Error('Calendar title must not be empty.');
    }
    await this.localProjection.renameLocalSource(source, normalizedTitle);
    await this.viewBackedApps.updateSourcePreference(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      source.id,
      { titleOverride: null }
    );
  }

  createMailboxLink(
    source: CalendarSourceRef,
    input: CalendarMailboxLinkCreateInput
  ): Promise<unknown> {
    return this.mutationOutbox.enqueueCreateMailboxLink(source, input);
  }

  updateMailboxLink(
    source: CalendarSourceRef,
    linkId: string,
    patch: CalendarMailboxLinkPatch
  ): Promise<unknown> {
    return this.mutationOutbox.enqueueUpdateMailboxLink(source, linkId, patch);
  }

  deleteMailboxLink(
    source: CalendarSourceRef,
    linkId: string
  ): Promise<unknown> {
    return this.mutationOutbox.enqueueDeleteMailboxLink(source, linkId);
  }

  async previewMailboxAreaTransition(
    workspaceId: string,
    source: CalendarSourceRef,
    linkId: string,
    input: {
      resource: CalendarMailboxAreaTransitionResource;
      targetAreaRootFolderId: string;
    }
  ): Promise<CalendarMailboxAreaTransitionPreview> {
    const localSources = this.loadLocalSourcesForPresentation();
    const preview = await this.calendarApi.previewMailboxAreaTransition(
      workspaceId,
      source,
      linkId,
      input
    );
    return this.preferLocalCalendarTitles(preview, await localSources);
  }

  async previewCalendarAreaTransition(
    workspaceId: string,
    source: CalendarSourceRef,
    targetAreaRootFolderId: string
  ): Promise<CalendarMailboxAreaTransitionPreview> {
    const localSources = this.loadLocalSourcesForPresentation();
    const preview = await this.calendarApi.previewCalendarAreaTransition(
      workspaceId,
      source,
      targetAreaRootFolderId
    );
    return this.preferLocalCalendarTitles(preview, await localSources);
  }

  transitionMailboxArea(
    workspaceId: string,
    source: CalendarSourceRef,
    linkId: string,
    input: {
      action: CalendarMailboxAreaTransitionAction;
      resource: CalendarMailboxAreaTransitionResource;
      targetAreaRootFolderId: string;
    }
  ): Promise<CalendarMailboxAreaTransitionResult> {
    return this.calendarApi.transitionMailboxArea(
      workspaceId,
      source,
      linkId,
      input
    );
  }

  transitionCalendarArea(
    workspaceId: string,
    source: CalendarSourceRef,
    input: {
      action: CalendarMailboxAreaTransitionAction;
      targetAreaRootFolderId: string;
    }
  ): Promise<CalendarMailboxAreaTransitionResult> {
    return this.calendarApi.transitionCalendarArea(workspaceId, source, input);
  }

  async previewMailboxAccountAreaTransition(
    workspaceId: string,
    mailAccountId: string,
    targetAreaRootFolderId: string
  ): Promise<CalendarMailboxAreaTransitionPreview | null> {
    const localSources = this.loadLocalSourcesForPresentation();
    const preview = await this.calendarApi.previewMailboxAccountAreaTransition(
      workspaceId,
      mailAccountId,
      targetAreaRootFolderId
    );
    return preview
      ? this.preferLocalCalendarTitles(preview, await localSources)
      : null;
  }

  transitionMailboxAccountArea(
    workspaceId: string,
    mailAccountId: string,
    input: {
      action: CalendarMailboxAreaTransitionAction;
      targetAreaRootFolderId: string;
    }
  ): Promise<CalendarMailboxAreaTransitionResult> {
    return this.calendarApi.transitionMailboxAccountArea(
      workspaceId,
      mailAccountId,
      input
    );
  }

  private async loadLocalSourcesForPresentation(): Promise<CalendarSource[]> {
    try {
      return (await this.localProjection.listLocalSources()).sources;
    } catch {
      return [];
    }
  }

  private preferLocalCalendarTitles(
    preview: CalendarMailboxAreaTransitionPreview,
    localSources: readonly CalendarSource[]
  ): CalendarMailboxAreaTransitionPreview {
    if (!preview.componentDetailsVisible || !preview.calendarRefs?.length) {
      return preview;
    }
    return {
      ...preview,
      calendarTitles: preview.calendarRefs.map((ref, index) => {
        const localSource = localSources.find(
          source =>
            source.docId === ref.calendarDocId &&
            source.databaseId === ref.calendarDatabaseId &&
            source.viewId === ref.calendarViewId
        );
        return (
          localSource?.title.trim() ||
          preview.calendarTitles[index]?.trim() ||
          'Calendar'
        );
      }),
    };
  }

  async respondToInvitation(
    source: CalendarSourceRef,
    rowId: string,
    status: CalendarInvitationResponseStatus,
    icsSequence?: number | null
  ): Promise<unknown> {
    const key = invitationResponseRefKey(source, rowId);
    this.invitationResponseOverlays.set(key, {
      icsSequence: icsSequence ?? null,
      rowId,
      source,
      status,
    });
    try {
      return await this.mutationOutbox.enqueueRespondInvitation(
        source,
        rowId,
        status,
        icsSequence
      );
    } catch (error) {
      this.invitationResponseOverlays.delete(key);
      throw error;
    }
  }

  removeSource(
    workspaceId: string,
    sourceId: string
  ): Promise<{ removed: boolean }> {
    return this.viewBackedApps.removeSource(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      workspaceId,
      sourceId
    );
  }

  retryFailed(id: string): Promise<void> {
    return this.mutationOutbox.retryFailed(id);
  }

  removeFailed(id: string): Promise<boolean> {
    return this.mutationOutbox.removeFailed(id);
  }

  calendarMutationInfo(
    mutation: CalendarMutationRecord
  ): CalendarMutationRecord {
    return mutation;
  }

  private calendarMutationOverlays(): CalendarMutationOverlay[] {
    return [
      ...this.mutationOutbox.pendingMutations$.value
        .filter(isCalendarMutation)
        .map(mutation => ({
          mutation,
          syncState: 'pending' as const,
        })),
      ...this.mutationOutbox.failedMutations$.value
        .filter(isCalendarMutation)
        .map(mutation => ({
          mutation,
          syncState: 'failed' as const,
        })),
    ].sort((left, right) => left.mutation.createdAt - right.mutation.createdAt);
  }

  private applyPendingMailboxLinkMutations(
    source: CalendarSource,
    links: readonly CalendarMailboxLink[]
  ): CalendarMailboxLink[] {
    const linksById = new Map(links.map(link => [link.id, link]));
    for (const { mutation, syncState } of this.calendarMutationOverlays()) {
      if (mutation.operation === 'calendar.createMailboxLink') {
        const payload = mutation.payload as {
          input: CalendarMailboxLinkCreateInput;
          source: CalendarSourceRef;
        };
        if (!sourceRefsEqual(source, payload.source)) continue;
        const existing =
          [...linksById.values()].find(
            link => link.mailAccountId === payload.input.mailAccountId
          ) ?? null;
        const linkId = existing?.id ?? `pending:${mutation.id}`;
        const inboundEnabled = payload.input.inboundEnabled ?? true;
        const outboundEnabled = payload.input.outboundEnabled ?? true;
        linksById.set(linkId, {
          ...failClosedPendingMailboxLinkFields(existing),
          id: linkId,
          inboundEnabled,
          mailAccountId: payload.input.mailAccountId,
          outboundEnabled,
          status: existing?.status ?? 'active',
          syncState,
        });
        continue;
      }

      if (mutation.operation === 'calendar.updateMailboxLink') {
        const payload = mutation.payload as {
          linkId: string;
          patch: CalendarMailboxLinkPatch;
          source: CalendarSourceRef;
        };
        if (!sourceRefsEqual(source, payload.source)) continue;
        const link = linksById.get(payload.linkId);
        if (!link) continue;
        linksById.set(payload.linkId, {
          ...link,
          ...payload.patch,
          syncState,
        });
        continue;
      }

      if (mutation.operation === 'calendar.deleteMailboxLink') {
        const payload = mutation.payload as {
          linkId: string;
          source: CalendarSourceRef;
        };
        if (!sourceRefsEqual(source, payload.source)) continue;
        const link = linksById.get(payload.linkId);
        if (!link) continue;
        linksById.set(payload.linkId, {
          ...link,
          syncState,
        });
      }
    }
    return [...linksById.values()].sort(compareMailboxLinks);
  }

  private applyPendingInvitationResponses(
    response: CalendarEventsResponse,
    sources: readonly CalendarSource[]
  ): CalendarEventsResponse {
    const sourceIdByRef = new Map(
      sources.map(source => [viewBackedAppSourceRefKey(source), source.id])
    );
    const responseByEventKey = new Map<
      string,
      CalendarInvitationResponseStatus
    >();
    const completedResponseByEventKey = new Map<
      string,
      CalendarInvitationResponseStatus
    >();
    for (const completed of this.invitationResponseOverlays.values()) {
      const sourceId = sourceIdByRef.get(
        viewBackedAppSourceRefKey(completed.source)
      );
      if (!sourceId) continue;
      completedResponseByEventKey.set(
        `${sourceId}:${completed.rowId}`,
        completed.status
      );
    }
    for (const mutation of this.mutationOutbox.pendingMutations$.value) {
      if (mutation.operation !== 'calendar.respondInvitation') continue;
      const payload = mutation.payload as {
        rowId: string;
        source: CalendarSourceRef;
        status: CalendarInvitationResponseStatus;
      };
      const sourceId = sourceIdByRef.get(
        viewBackedAppSourceRefKey(payload.source)
      );
      if (!sourceId) continue;
      responseByEventKey.set(`${sourceId}:${payload.rowId}`, payload.status);
    }
    if (
      responseByEventKey.size === 0 &&
      completedResponseByEventKey.size === 0
    ) {
      return response;
    }
    return {
      ...response,
      events: response.events.flatMap(event => {
        const eventKey = `${event.sourceId}:${event.rowId}`;
        const completedStatus = completedResponseByEventKey.get(eventKey);
        if (completedStatus === 'declined') return [];
        if (completedStatus) {
          return [
            {
              ...event,
              invitationState: completedStatus,
              sourceType: null,
            },
          ];
        }
        const pendingStatus = responseByEventKey.get(eventKey);
        return pendingStatus
          ? [{ ...event, invitationState: pendingStatus }]
          : [event];
      }),
    };
  }

  private reconcileCompletedInvitationResponses(
    response: CalendarEventsResponse,
    sources: readonly CalendarSource[]
  ): void {
    const sourceIdByRef = new Map(
      sources.map(source => [viewBackedAppSourceRefKey(source), source.id])
    );
    const eventByKey = new Map(
      response.events.map(event => [`${event.sourceId}:${event.rowId}`, event])
    );
    for (const [key, completed] of this.invitationResponseOverlays) {
      const sourceId = sourceIdByRef.get(
        viewBackedAppSourceRefKey(completed.source)
      );
      if (!sourceId) continue;
      const event = eventByKey.get(`${sourceId}:${completed.rowId}`);
      const superseded =
        event &&
        completed.icsSequence !== null &&
        event.icsSequence !== null &&
        event.icsSequence > completed.icsSequence;
      const confirmed =
        completed.status === 'declined'
          ? !event
          : event?.sourceType !== 'mail_invitation_preview' &&
            event?.invitationState === completed.status;
      if (superseded || confirmed) {
        this.invitationResponseOverlays.delete(key);
      }
    }
  }
}

/** Free canvas space between the page's main note and a new calendar. */
const CALENDAR_CANVAS_GAP = 200;

export async function provisionLocalCalendarDatabase(
  doc: Store,
  input: {
    /** Canvas block the calendar sits to the right of (the page's main note). */
    besideBlockId: string;
    databaseId?: string;
    parentId?: string;
    title: string;
  }
): Promise<LocalCalendarDatabaseProvision> {
  const beside = doc.getModelById(input.besideBlockId);
  const besideXYWH = (beside?.props as { xywh?: unknown } | undefined)?.xywh;
  if (typeof besideXYWH !== 'string') {
    throw new Error('Calendar anchor block has no canvas position.');
  }
  const [anchorX, anchorY, anchorWidth] = deserializeXYWH(besideXYWH);
  let viewId: string | null = null;
  const provisioned = await provisionShardedDatabaseBlockDataSource(doc, {
    databaseId: input.databaseId,
    parentId: input.parentId,
    props: {
      xywh: serializeXYWH(
        anchorX + anchorWidth + CALENDAR_CANVAS_GAP,
        anchorY,
        DEFAULT_CANVAS_DATABASE_WIDTH,
        DEFAULT_CANVAS_DATABASE_HEIGHT
      ),
    },
    title: input.title,
    initDataSource: dataSource => {
      const calendarViewId = dataSource.viewManager.viewAdd('calendar');
      const calendarView = dataSource.viewManager.viewGet(calendarViewId);
      if (!calendarView || calendarView.type !== 'calendar') {
        throw new Error('Calendar view was not provisioned locally.');
      }
      calendarView.nameSet(input.title);
      (calendarView as CalendarSingleView).ensureCalendarColumns();

      const boardViewId = dataSource.viewManager.viewAdd('kanban');
      const boardView = dataSource.viewManager.viewGet(boardViewId);
      if (!boardView || boardView.type !== 'kanban') {
        throw new Error('Board view was not provisioned locally.');
      }
      boardView.nameSet('Board');

      const timelineViewId = dataSource.viewManager.viewAdd('gantt');
      const timelineView = dataSource.viewManager.viewGet(timelineViewId);
      if (!timelineView || timelineView.type !== 'gantt') {
        throw new Error('Timeline view was not provisioned locally.');
      }
      timelineView.nameSet('Timeline');
      const timeline = timelineView as GanttSingleView;
      const calendar = calendarView as CalendarSingleView;
      timeline.setStartDateCol(calendar.startDateCol$.value);
      timeline.setEndDateCol(calendar.endDateCol$.value);
      timeline.setProgressCol(calendar.progressCol$.value);
      timeline.setStatusCol(calendar.statusCol$.value);
      timeline.setAssigneeCol(calendar.assigneeCol$.value);
      timeline.setDependenciesCol(calendar.dependenciesCol$.value);

      dataSource.viewManager.setCurrentView(calendarViewId);
      // The canvas shows its own presentation view, not the current view.
      presentCanvasDatabaseView(dataSource, calendarViewId);
      viewId = calendarViewId;
    },
  });

  if (provisioned.status === 'discarded')
    throw new PublicationError('CANCELLED');
  if (!viewId) {
    throw new Error('Calendar view id was not created locally.');
  }

  return {
    blockId: provisioned.databaseBlockId,
    databaseId: provisioned.referenceProps.databaseId,
    viewId,
  };
}

function normalizeCalendarTitle(title: string): string {
  const normalized = title.trim();
  if (!normalized) {
    throw new Error('Calendar title must not be empty.');
  }
  return normalized;
}

function failClosedPendingMailboxLinkFields(
  existing: CalendarMailboxLink | null
): Pick<
  CalendarMailboxLink,
  | 'canConfigure'
  | 'canSend'
  | 'canSync'
  | 'mailbox'
  | 'mailboxVisibility'
  | 'operationPolicies'
  | 'policy'
  | 'policyReasons'
> {
  return {
    canConfigure: false,
    canSend: false,
    canSync: false,
    mailbox: existing?.mailbox ?? null,
    mailboxVisibility: existing?.mailboxVisibility ?? 'hidden',
    operationPolicies: existing?.operationPolicies ?? {
      configure: {
        allowed: false,
        reasons: ['resource_unavailable'],
      },
      send: {
        allowed: false,
        reasons: ['resource_unavailable'],
      },
      sync: {
        allowed: false,
        reasons: ['resource_unavailable'],
      },
    },
    policy: existing?.policy ?? {
      allowed: false,
      reasons: ['resource_unavailable'],
    },
    policyReasons: existing?.policyReasons ?? ['resource_unavailable'],
  };
}

function compareMailboxLinks(
  left: CalendarMailboxLink,
  right: CalendarMailboxLink
): number {
  return (
    left.mailAccountId.localeCompare(right.mailAccountId) ||
    left.id.localeCompare(right.id)
  );
}

function mergeCalendarSources(
  cachedSources: readonly CalendarSource[],
  currentSources: readonly CalendarSource[]
): CalendarSource[] {
  const sourcesByRef = new Map(
    cachedSources.map(source => [viewBackedAppSourceRefKey(source), source])
  );
  for (const source of currentSources) {
    const cached = sourcesByRef.get(viewBackedAppSourceRefKey(source));
    const area = source.area ?? cached?.area ?? null;
    sourcesByRef.set(viewBackedAppSourceRefKey(source), {
      ...cached,
      ...source,
      area,
      color:
        source.syncState === 'local'
          ? (cached?.color ?? source.color)
          : (source.color ?? cached?.color ?? null),
      visible:
        source.syncState === 'local'
          ? (cached?.visible ?? source.visible)
          : source.visible,
      projectionError:
        source.projectionError === 'area-unavailable' && area
          ? null
          : source.projectionError,
    });
  }
  return [...sourcesByRef.values()].sort(
    (left, right) =>
      left.title.localeCompare(right.title) || left.id.localeCompare(right.id)
  );
}

function mergeCalendarCandidates(
  cachedCandidates: readonly CalendarSourceCandidate[],
  currentCandidates: readonly CalendarSourceCandidate[]
): CalendarSourceCandidate[] {
  const candidatesByRef = new Map(
    cachedCandidates.map(candidate => [
      viewBackedAppSourceRefKey(candidate),
      candidate,
    ])
  );
  for (const candidate of currentCandidates) {
    const cached = candidatesByRef.get(viewBackedAppSourceRefKey(candidate));
    candidatesByRef.set(viewBackedAppSourceRefKey(candidate), {
      ...cached,
      ...candidate,
      area: candidate.area ?? cached?.area ?? null,
    });
  }
  return [...candidatesByRef.values()].sort(
    (left, right) =>
      left.title.localeCompare(right.title) ||
      viewBackedAppSourceRefKey(left).localeCompare(
        viewBackedAppSourceRefKey(right)
      )
  );
}

function mergeCalendarEvents(
  localEvents: CalendarEventsResponse,
  serverEvents: CalendarEventsResponse,
  localEventSource: 'cache' | 'local-projection'
): CalendarEventsResponse {
  const authoritativeLocalSourceIds =
    localEventSource === 'local-projection'
      ? new Set(
          localEvents.sources
            .filter(source => source.error === null)
            .map(source => source.id)
        )
      : new Set<string>();
  const eventsById = new Map<string, CalendarProjectedEvent>();
  for (const event of serverEvents.events) {
    if (
      authoritativeLocalSourceIds.has(event.sourceId) &&
      event.sourceType !== 'mail_invitation_preview'
    ) {
      // A successfully projected local database is canonical for ordinary
      // calendar rows. Keeping a server/cached row that is absent locally
      // resurrects a just-deleted event until the async reconcile completes.
      continue;
    }
    eventsById.set(event.id, event);
  }
  for (const event of localEvents.events) {
    const serverEvent = eventsById.get(event.id);
    if (
      serverEvent?.sourceType === 'mail_invitation_preview' &&
      event.sourceType !== 'mail_invitation_preview'
    ) {
      // A mailbox-linked calendar row can already exist locally while the
      // server still projects the same row as an actionable invitation. The
      // local row does not carry the mail-only RSVP metadata and must not
      // erase it. Once the server confirms a response it returns a normal
      // event too, allowing the local projection to resume as usual.
      continue;
    }
    if (event.sourceType === 'mail_invitation_preview') {
      // Invitation previews are projected by the server. Once a server
      // response succeeds, its absence means removal and its sequence is the
      // canonical lower bound. Keep a locally newer projection only while the
      // server catches up; never revive an older or removed preview.
      if (!serverEvent) continue;
      if (serverEvent.sourceType !== 'mail_invitation_preview') continue;
      if ((event.icsSequence ?? -1) <= (serverEvent.icsSequence ?? -1)) {
        continue;
      }
    }
    eventsById.set(event.id, event);
  }

  const sourcesById = new Map<
    string,
    CalendarEventsResponse['sources'][number]
  >();
  for (const source of serverEvents.sources) {
    sourcesById.set(source.id, source);
  }
  for (const source of localEvents.sources) {
    const serverSource = sourcesById.get(source.id);
    // An initial local projection may still be computing after the server
    // supplied the complete window. Its pending marker is not evidence of
    // unsynced data and must not overwrite that canonical source state.
    // Local rows remain usable while their server projection materializes.
    // Do not erase the canonical pending state: it owns both the sync notice
    // and the next projection read, even when the local read already succeeded.
    sourcesById.set(source.id, {
      ...source,
      error:
        serverSource &&
        (localEventSource === 'cache' || source.error === 'pending')
          ? serverSource.error
          : serverSource?.error === 'pending'
            ? 'pending'
            : source.error,
    });
  }

  return {
    calendarEventContractVersion: 2,
    events: [...eventsById.values()].sort((left, right) =>
      left.startAt.localeCompare(right.startAt)
    ),
    sources: [...sourcesById.values()],
    window: localEvents.window,
  };
}

function sourceRefsEqual(
  left: Pick<CalendarSource, 'blockId' | 'databaseId' | 'docId' | 'viewId'>,
  right: CalendarSourceRef
): boolean {
  return viewBackedAppSourceRefKey(left) === viewBackedAppSourceRefKey(right);
}

function invitationResponseRefKey(
  source: CalendarSourceRef,
  rowId: string
): string {
  return `${viewBackedAppSourceRefKey(source)}:${rowId}`;
}
